import type { KeycloakOnLoad } from 'keycloak-js'

export enum Environments {
  local = 'local',
  devci = 'devci',
  staging = 'staging',
  prod = 'prod',
}

export const Locations = {
  LOCAL: 'local',
  LEARNING: 'learning',
  CKLA: 'ckla',
}

export function getEnv() {
  const hostName = window.location.hostname
  let env = Environments.prod

  if (hostName.includes('local')) {
    env = Environments.local
  } else if (hostName.includes('devci.') || hostName.includes('.ci.')) {
    env = Environments.devci
  } else if (hostName.includes('staging.')) {
    env = Environments.staging
  }

  return env
}

export const isLocalEnvByUrl = () => {
  const url = window.location.hostname

  return url.includes('local.amplify.com')
}

export function getLocation() {
  const hostName = window.location.hostname

  if (hostName.includes('local')) {
    return Locations.LOCAL
  }
  if (hostName.includes('ckla.')) {
    return Locations.CKLA
  }
  return Locations.LEARNING
}

export function isInternalEnv() {
  return getEnv() !== Environments.prod
}

export function getDdRumConfig() {
  return {
    applicationId: '35172406-ca77-4231-8d4a-2489312ad3e4',
    clientToken: 'pubc235b47975e69659d3c4ebe22f669612',
    site: 'datadoghq.com',
    service: 'ckla-hub',
    sampleRate: 100,
    // 43b0821990351d686d97652b0798ebe4b47ce0f4 is interpolated via sed in terraform-config
    version: '43b0821990351d686d97652b0798ebe4b47ce0f4',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    env: getEnv(),
  }
}

/**
 * Used to force epub.js request to pass cookies
 */
export const enableWithCredentialsForEpub = () => {
  ;((open) => {
    // overwrite default open method
    // @ts-ignore
    // eslint-disable-next-line func-names
    XMLHttpRequest.prototype.open = function (method, url, async, user, pass) {
      // TS Updates caused this error. 'URL type doesnt contain .includes()'
      if (url.toString().includes('.epub')) {
        this.withCredentials = true
      }
      // make request
      open.call(this, method, url, async, user, pass)
    }
  })(XMLHttpRequest.prototype.open)
}

const config = {
  [Environments.local]: {
    ENVIRONMENT: 'devci',
    HUB_URL: `https://local.amplify.com:3000/hub`,
    CKLA_URL: 'https://ckla.ci.amplify.com',
    CKLA_LANDING_URL: 'https://ckla.ci.amplify.com',
    ACCOUNT_URL: 'https://my-devci.poc.learning.amplify.com',
    VOCAB_URL:
      'https://vocab-app-devci.poc.learning.amplify.com/vocab_app/#/home?externalSource=cklaHub',
    READING_ROOT_URL: 'https://reading.ar.ci.amplify.com',
    LEARNING_ROOT_URL: 'https://apps-devci.poc.learning.amplify.com',
    API_BASE: 'https://api-devci.poc.learning.amplify.com',
    RETAIL_URL: 'https://api-devci.poc.learning.amplify.com/retail',
    AUTH_LINK: 'https://my-devci.poc.learning.amplify.com/auth',
    LEARNING_URL: 'https://learning-devci.poc.learning.amplify.com/',
    STUDENT_HOME_URL: 'https://learning-devci.poc.learning.amplify.com/home',
    LEARNING_HUB_URL: 'https://learning-devci.poc.learning.amplify.com/hub',
    ONBOARDING: 'https://api-devci.poc.learning.amplify.com/onboarding-service',
  },
  [Environments.devci]: {
    ENVIRONMENT: 'devci',
    HUB_URL: 'https://ckla.ci.amplify.com/hub',
    CKLA_LANDING_URL: 'https://ckla.amplify.com',
    CKLA_URL: 'https://ckla.ci.amplify.com',
    ACCOUNT_URL: 'https://my-devci.poc.learning.amplify.com',
    VOCAB_URL:
      'https://vocabulary-devci.poc.learning.amplify.com/#/home?externalSource=cklaHub',
    READING_ROOT_URL: 'https://reading.ar.ci.amplify.com',
    LEARNING_ROOT_URL: 'https://apps-devci.poc.learning.amplify.com',
    API_BASE: 'https://api-devci.poc.learning.amplify.com',
    RETAIL_URL: 'https://api-devci.poc.learning.amplify.com/retail',
    AUTH_LINK: 'https://my-devci.poc.learning.amplify.com/auth',
    LEARNING_URL: 'https://learning-devci.poc.learning.amplify.com',
    STUDENT_HOME_URL: 'https://learning-devci.poc.learning.amplify.com/home',
    LEARNING_HUB_URL: 'https://learning-devci.poc.learning.amplify.com/hub',
    ONBOARDING: 'https://api-devci.poc.learning.amplify.com/onboarding-service',
  },
  [Environments.staging]: {
    ENVIRONMENT: 'staging',
    HUB_URL: 'https://ckla.staging.amplify.com/hub',
    CKLA_LANDING_URL: 'https://ckla.amplify.com',
    CKLA_URL: 'https://ckla.staging.amplify.com',
    ACCOUNT_URL: 'https://my-staging.stage.learning.amplify.com',
    VOCAB_URL:
      'https://vocabulary-staging.stage.learning.amplify.com/#/home?externalSource=cklaHub',
    READING_ROOT_URL: 'https://reading.ar.staging.amplify.com',
    LEARNING_ROOT_URL: 'https://apps-staging.stage.learning.amplify.com',
    API_BASE: 'https://api-staging.stage.learning.amplify.com',
    RETAIL_URL: 'https://api-staging.stage.learning.amplify.com/retail',
    AUTH_LINK: 'https://my-staging.stage.learning.amplify.com/auth',
    LEARNING_URL: 'https://learning-staging.stage.learning.amplify.com',
    STUDENT_HOME_URL:
      'https://learning-staging.stage.learning.amplify.com/home',
    LEARNING_HUB_URL: 'https://learning-staging.stage.learning.amplify.com/hub',
    ONBOARDING:
      'https://api-staging.stage.learning.amplify.com/onboarding-service',
  },
  [Environments.prod]: {
    ENVIRONMENT: 'prod',
    HUB_URL: 'https://ckla.amplify.com/hub',
    CKLA_LANDING_URL: 'https://ckla.amplify.com',
    CKLA_URL: 'https://ckla.amplify.com',
    ACCOUNT_URL: 'https://my.amplify.com',
    VOCAB_URL: 'https://vocabulary.amplify.com/#/home?externalSource=cklaHub',
    READING_ROOT_URL: 'https://reading.amplify.com',
    LEARNING_ROOT_URL: 'https://apps.learning.amplify.com',
    API_BASE: 'https://api.learning.amplify.com',
    RETAIL_URL: 'https://api.learning.amplify.com/retail',
    AUTH_LINK: 'https://my.amplify.com/auth',
    LEARNING_URL: 'https://learning.amplify.com',
    STUDENT_HOME_URL: 'https://learning.amplify.com/home',
    LEARNING_HUB_URL: 'https://learning.amplify.com/hub',
    ONBOARDING: 'https://api.learning.amplify.com/onboarding-service',
  },
}

const currentEnv = getEnv()
export const EnvConstants = config[currentEnv]

export const keycloakConfig = {
  realm: 'Amplify',
  url: EnvConstants.AUTH_LINK,
  clientId: 'ckla-hub',
}

export const keycloakInitConfig = {
  checkLoginIframe: true,
  onLoad: 'login-required' as KeycloakOnLoad | undefined,
  token: undefined as undefined | string,
  refreshToken: undefined as undefined | string,
  enableLogging: undefined as undefined | true,
}

export default EnvConstants
